import { toRelativeUrl } from "@okta/okta-auth-js";
import { useOktaAuth } from "@okta/okta-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, Outlet } from "react-router-dom";

import { rootPaths } from "./routePaths";
import { useGetUserPermission } from "../api/permission";
import { IdleWarningSnackbar } from "../components/atoms/IdleWarningSnackbar";
import Loading from "../components/molecules/Loading";
import { IDLE_MS, WARN_MS } from "../config";
import { MODULE } from "../constants/constants";
import { useIdleLogout } from "../hooks/useIdleLogout";
import { UserActionsResponse } from "../models/Authorisation";
import { usePermissionStore } from "../stores/permissionStore";
import { useUserStore } from "../stores/userStore";

export const RequiredAuth = () => {
  const { oktaAuth, authState } = useOktaAuth();
  const [
    user,
    setUser,
    setUserActions,
    setUserRole,
    setCountry,
    setLeadsUserAction,
  ] = useUserStore((state) => [
    state.user,
    state.setUser,
    state.setUserActions,
    state.setUserRole,
    state.setCountry,
    state.setLeadsUserAction,
  ]);

  const { permissionToken, setPermission } = usePermissionStore();

  const {
    data: userPermission,
    isLoading,
    isError,
    error,
  } = useGetUserPermission({ enabled: !!authState?.isAuthenticated } as any);

  useEffect(() => {
    if (userPermission) {
      const permissionInventory = userPermission.data?.permissions?.find(
        (item: UserActionsResponse) => item.module === MODULE.INVENTORY
      );
      const permissionLeads = userPermission.data?.permissions?.find(
        (item: UserActionsResponse) => item.module === MODULE.LEADS
      );
      setUserActions(permissionInventory?.allowedActions);
      setLeadsUserAction(permissionLeads?.allowedActions.actions);
      setUserRole(permissionInventory?.userRole);
      setPermission(userPermission.data);
      setCountry(permissionInventory?.countries);
    }
  }, [userPermission]);

  const redirectStartedRef = useRef(false);

  useEffect(() => {
    if (!authState) return;

    if (!authState.isAuthenticated && !redirectStartedRef.current) {
      redirectStartedRef.current = true;
      const originalUri = toRelativeUrl(window.location.href, window.location.origin);
      oktaAuth.setOriginalUri(originalUri);
      (async () => {
        try {
          await oktaAuth.signInWithRedirect();
        } catch (e) {}
      })();
    }

    if (authState && authState.isAuthenticated) {
      redirectStartedRef.current = false;
      oktaAuth
        .getUser()
        .then((info) => {
          if (!user?.name) {
            setUser({
              name: info.name,
              email: info.email,
              id: info.sub,
            });
          }
        })
        .catch((err) => {
          console.error(err)
        });
    }
  }, [authState?.isAuthenticated]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "okta-token-storage") {
        try {
          oktaAuth.authStateManager.updateAuthState();
        } catch {}
      }
    };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
    };
  }, [oktaAuth]);

  const [idleWarnOpen, setIdleWarnOpen] = useState(false);
  const [remainingMs, setRemainingMs] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (authState?.isAuthenticated) {
      setIdleWarnOpen(false);
      setRemainingMs(undefined);
    }
  }, [authState?.isAuthenticated]);

  const onWarnStable = useCallback((msLeft: number) => {
    setRemainingMs(msLeft);
    setIdleWarnOpen(true);
  }, []);

  useIdleLogout(oktaAuth, {
    idleMs: IDLE_MS,
    warnMs: WARN_MS,
    onWarn: onWarnStable,
    events: ["pointerdown", "click", "keydown", "touchstart", "mousemove"],
  });

  const handleIdleWarnClose = () => {
    setIdleWarnOpen(false);
  };

  const handleContinueSession = () => {
    try {
      localStorage.setItem("app_last_active_ts", String(Date.now()));
      document.dispatchEvent(new Event("pointerdown"));
      document.dispatchEvent(new Event("mousemove"));
    } catch {}
    setIdleWarnOpen(false);
  };

  if (!authState || !authState?.isAuthenticated || isLoading || !permissionToken) {
    return <Loading />;
  }

  if (isError && error) {
    return <Navigate to={rootPaths.unauthorised} />;
  }

  return (
    <>
      <Outlet />
      <IdleWarningSnackbar
        open={idleWarnOpen}
        remainingMs={remainingMs}
        onClose={handleIdleWarnClose}
        onContinue={handleContinueSession}
      />
    </>
  );
};